/**
 * Full mesh topology manager for mrmr.
 *
 * Manages up to MAX_PARTICIPANTS peer connections in a full mesh,
 * handles third-party SDP relay, heartbeats, bandwidth reports,
 * and connection quality monitoring.
 */

import {
  MAX_PARTICIPANTS,
  HEARTBEAT_INTERVAL_MS,
} from '../constants'
import {
  createPeerConnection,
  type PeerConnectionEvents,
  type PeerConnectionHandleInternal,
  type ConnectionQuality,
  type QualitySnapshot,
} from './webrtc'
import {
  createEnvelope,
  sendMessage,
  onMessage,
} from './datachannel'
import type { DataChannels } from './datachannel'
import type {
  SignalingEnvelope,
  HeartbeatMessage,
  SdpOfferMessage,
  SdpAnswerMessage,
  IceCandidateMessage,
  ParticipantJoinedMessage,
  ParticipantLeftMessage,
  MeetingStateSyncMessage,
  ParticipantUpdateMessage,
} from '../types'

// ============================================================
// Types
// ============================================================

export interface PeerInfo {
  peerId: string
  displayName: string
  publicKey: string
  audioMuted: boolean
  videoMuted: boolean
  joinedAt: number
  quality: ConnectionQuality
}

export interface MeshEvents {
  /** New peer fully connected with DataChannels open */
  onPeerConnected?: (peerId: string, channels: DataChannels) => void
  /** Peer disconnected or lost */
  onPeerDisconnected?: (peerId: string, reason: 'left' | 'timeout' | 'error') => void
  /** Remote media track received */
  onTrack?: (peerId: string, event: RTCTrackEvent) => void
  /** Participant info updated (mute state, name) */
  onParticipantUpdate?: (peerId: string, info: PeerInfo) => void
  /** Meeting state sync received (for late joiners) */
  onMeetingStateSync?: (msg: MeetingStateSyncMessage) => void
  /** SDP needs to be relayed to a peer we're connected to */
  onSdpRelay?: (targetPeerId: string, msg: SignalingEnvelope) => void
  /** Connection quality changed for a peer */
  onQualityChange?: (peerId: string, quality: ConnectionQuality) => void
}

export interface MeshManager {
  /** Local peer ID */
  readonly localPeerId: string
  /** Add a new peer connection (after Trystero discovery) */
  addPeer(remotePeerId: string): PeerConnectionHandleInternal | null
  /** Remove a peer and clean up */
  removePeer(peerId: string, reason: 'left' | 'timeout' | 'error'): void
  /** Get a peer's connection handle */
  getPeer(peerId: string): PeerConnectionHandleInternal | undefined
  /** Get all connected peers */
  getPeers(): Map<string, PeerConnectionHandleInternal>
  /** Get peer info map */
  getPeerInfo(): Map<string, PeerInfo>
  /** Update peer display info */
  updatePeerInfo(peerId: string, update: Partial<PeerInfo>): void
  /** Add media tracks to all connections (on Join click) */
  addTracksToAll(stream: MediaStream): void
  /** Handle inbound SDP from a remote peer */
  handleRemoteSdp(peerId: string, description: RTCSessionDescriptionInit): Promise<void>
  /** Handle inbound ICE candidate from a remote peer */
  handleRemoteIceCandidate(peerId: string, candidate: RTCIceCandidateInit): Promise<void>
  /** Relay an SDP offer/answer through us to another peer */
  relaySdp(fromPeerId: string, msg: SdpOfferMessage | SdpAnswerMessage): void
  /** Relay an ICE candidate through us to another peer */
  relayIceCandidate(fromPeerId: string, msg: IceCandidateMessage): void
  /** Broadcast a message to all connected peers on the signaling channel */
  broadcastSignaling(type: string, payload: unknown): void
  /** Broadcast a message to all connected peers on the health channel */
  broadcastHealth(type: string, payload: unknown): void
  /** Send a message to a specific peer on the signaling channel */
  sendToSignaling(peerId: string, type: string, payload: unknown): void
  /** Send participant-joined to all peers */
  broadcastParticipantJoined(info: ParticipantJoinedMessage['payload']): void
  /** Send participant-left to all peers */
  broadcastParticipantLeft(reason: 'user-left' | 'kicked' | 'error'): void
  /** Send participant-update to all peers */
  broadcastParticipantUpdate(update: ParticipantUpdateMessage['payload']): void
  /** Send meeting-state-sync to a specific peer */
  sendMeetingStateSync(peerId: string, payload: MeetingStateSyncMessage['payload']): void
  /** Start heartbeat sending on all connections */
  startHeartbeats(): void
  /** Stop heartbeats */
  stopHeartbeats(): void
  /** Get quality snapshot for a peer */
  getQuality(peerId: string): QualitySnapshot | null
  /** Get the number of connected peers */
  peerCount(): number
  /** Check if we can accept more peers */
  canAcceptPeer(): boolean
  /** Close all connections and clean up */
  destroy(): void
}

// ============================================================
// Bandwidth report interval
// ============================================================

const BANDWIDTH_REPORT_INTERVAL_MS = 10_000

// ============================================================
// Mesh manager factory
// ============================================================

export function createMeshManager(
  localPeerId: string,
  events: MeshEvents = {},
): MeshManager {
  const peers = new Map<string, PeerConnectionHandleInternal>()
  const peerInfoMap = new Map<string, PeerInfo>()

  let heartbeatInterval: ReturnType<typeof setInterval> | null = null
  let bandwidthReportInterval: ReturnType<typeof setInterval> | null = null
  let joinedAt = Date.now()

  // --- Peer connection events wired into the mesh ---

  function createPeerEvents(_remotePeerId: string): PeerConnectionEvents {
    return {
      onTrack: (peerId, event) => {
        events.onTrack?.(peerId, event)
      },

      onConnectionStateChange: (peerId, state) => {
        if (state === 'connected') {
          const handle = peers.get(peerId)
          if (handle) {
            const prev = peerInfoMap.get(peerId)
            if (prev) {
              const newQuality = handle.getQuality()
              if (prev.quality !== newQuality) {
                prev.quality = newQuality
                events.onQualityChange?.(peerId, newQuality)
              }
            }
          }
        }
      },

      onIceCandidate: (peerId, candidate) => {
        if (!candidate) return
        const handle = peers.get(peerId)
        if (!handle?.channels) return

        const msg = createEnvelope('ice-candidate', localPeerId, {
          targetPeerId: peerId,
          candidate: candidate.candidate,
          sdpMid: candidate.sdpMid,
          sdpMLineIndex: candidate.sdpMLineIndex,
          usernameFragment: candidate.usernameFragment ?? null,
        })
        sendMessage(handle.channels.signaling, msg)
      },

      onNegotiationNeeded: (peerId, description) => {
        const handle = peers.get(peerId)
        if (!handle?.channels) return

        const type = description.type === 'offer' ? 'sdp-offer' : 'sdp-answer'
        const msg = createEnvelope(type, localPeerId, {
          targetPeerId: peerId,
          originPeerId: localPeerId,
          sdp: description.sdp,
          sdpType: description.type,
          iceUfrag: '',
          icePwd: '',
        })
        sendMessage(handle.channels.signaling, msg)
      },

      onChannelsReady: (peerId, channels) => {
        wireChannelHandlers(peerId, channels)
        events.onPeerConnected?.(peerId, channels)
      },

      onPeerTimeout: (peerId) => {
        // Peer missed heartbeats — mark degraded and attempt ICE restart
        console.warn(`[mesh] Peer ${peerId} heartbeat timeout — attempting ICE restart`)
        const info = peerInfoMap.get(peerId)
        if (info && info.quality !== 'critical') {
          info.quality = 'critical'
          events.onQualityChange?.(peerId, 'critical')
        }
        const h = peers.get(peerId)
        if (h && h.pc.connectionState !== 'closed') {
          h.pc.restartIce()
        }
      },

      onPeerDead: (peerId) => {
        // Peer is dead — clean up
        console.warn(`[mesh] Peer ${peerId} declared dead`)
        removePeer(peerId, 'timeout')
      },

      onPeerLost: (peerId) => {
        // ICE restart exhausted
        removePeer(peerId, 'error')
      },
    }
  }

  // --- Wire DataChannel message handlers for a connected peer ---

  function wireChannelHandlers(peerId: string, channels: DataChannels): void {
    onMessage(channels, {
      // SDP relay: offers/answers targeted at other peers
      'sdp-offer': (msg) => {
        const sdpMsg = msg as SdpOfferMessage
        if (sdpMsg.payload.targetPeerId === localPeerId) {
          // This SDP is for us
          const handle = peers.get(peerId)
          handle?.handleRemoteDescription({
            type: 'offer',
            sdp: sdpMsg.payload.sdp,
          })
        } else {
          // Relay to the target peer
          relaySdp(peerId, sdpMsg)
        }
      },

      'sdp-answer': (msg) => {
        const sdpMsg = msg as SdpAnswerMessage
        if (sdpMsg.payload.targetPeerId === localPeerId) {
          const handle = peers.get(peerId)
          handle?.handleRemoteDescription({
            type: 'answer',
            sdp: sdpMsg.payload.sdp,
          })
        } else {
          relaySdp(peerId, sdpMsg)
        }
      },

      'ice-candidate': (msg) => {
        const iceMsg = msg as IceCandidateMessage
        if (iceMsg.payload.targetPeerId === localPeerId) {
          const handle = peers.get(peerId)
          if (iceMsg.payload.candidate === '') {
            // End-of-candidates signal
            handle?.handleIceCandidate({ candidate: '' })
          } else {
            handle?.handleIceCandidate({
              candidate: iceMsg.payload.candidate,
              sdpMid: iceMsg.payload.sdpMid,
              sdpMLineIndex: iceMsg.payload.sdpMLineIndex,
            })
          }
        } else {
          relayIceCandidate(peerId, iceMsg)
        }
      },

      'participant-joined': (msg) => {
        const pjMsg = msg as ParticipantJoinedMessage
        const newPeerId = pjMsg.payload.peerId
        // If we don't already have a connection to this peer, create one
        if (newPeerId !== localPeerId && !peers.has(newPeerId)) {
          addPeer(newPeerId)
          updatePeerInfo(newPeerId, {
            displayName: pjMsg.payload.displayName,
            publicKey: pjMsg.payload.publicKey,
          })
        }
      },

      'participant-left': (msg) => {
        const plMsg = msg as ParticipantLeftMessage
        removePeer(plMsg.from, 'left')
      },

      'participant-update': (msg) => {
        const puMsg = msg as ParticipantUpdateMessage
        const info = peerInfoMap.get(puMsg.from)
        if (info) {
          if (puMsg.payload.audioMuted !== null) info.audioMuted = puMsg.payload.audioMuted
          if (puMsg.payload.videoMuted !== null) info.videoMuted = puMsg.payload.videoMuted
          if (puMsg.payload.displayName !== null) info.displayName = puMsg.payload.displayName
          events.onParticipantUpdate?.(puMsg.from, info)
        }
      },

      'meeting-state-sync': (msg) => {
        events.onMeetingStateSync?.(msg as MeetingStateSyncMessage)
      },

      'heartbeat': (msg) => {
        const handle = peers.get(peerId) as PeerConnectionHandleInternal | undefined
        handle?.resetHeartbeatTimer()

        // Send heartbeat-ack
        const hbMsg = msg as HeartbeatMessage
        const ack = createEnvelope('heartbeat-ack', localPeerId, {
          echoTs: hbMsg.ts,
        })
        try {
          sendMessage(channels.health, ack)
        } catch {
          // Channel may not be open
        }
      },

      'heartbeat-ack': () => {
        // RTT tracking handled by quality monitor via getStats()
      },
    })
  }

  // --- Public API ---

  function addPeer(remotePeerId: string): PeerConnectionHandleInternal | null {
    if (peers.has(remotePeerId)) {
      return peers.get(remotePeerId)!
    }

    if (!canAcceptPeer()) {
      console.warn(`[mesh] Cannot accept peer ${remotePeerId}: at capacity (${MAX_PARTICIPANTS})`)
      return null
    }

    const peerEvents = createPeerEvents(remotePeerId)
    const handle = createPeerConnection(localPeerId, remotePeerId, peerEvents) as PeerConnectionHandleInternal

    peers.set(remotePeerId, handle)
    peerInfoMap.set(remotePeerId, {
      peerId: remotePeerId,
      displayName: '',
      publicKey: '',
      audioMuted: false,
      videoMuted: false,
      joinedAt: Date.now(),
      quality: 'good',
    })

    return handle
  }

  function removePeer(peerId: string, reason: 'left' | 'timeout' | 'error'): void {
    const handle = peers.get(peerId)
    if (!handle) return

    handle.close()
    peers.delete(peerId)
    peerInfoMap.delete(peerId)
    events.onPeerDisconnected?.(peerId, reason)
  }

  function getPeer(peerId: string): PeerConnectionHandleInternal | undefined {
    return peers.get(peerId)
  }

  function getPeers(): Map<string, PeerConnectionHandleInternal> {
    return peers
  }

  function getPeerInfo(): Map<string, PeerInfo> {
    return peerInfoMap
  }

  function updatePeerInfo(peerId: string, update: Partial<PeerInfo>): void {
    const info = peerInfoMap.get(peerId)
    if (info) {
      Object.assign(info, update)
    }
  }

  function addTracksToAll(stream: MediaStream): void {
    for (const handle of peers.values()) {
      handle.addTracks(stream)
    }
  }

  async function handleRemoteSdp(
    peerId: string,
    description: RTCSessionDescriptionInit,
  ): Promise<void> {
    const handle = peers.get(peerId)
    if (handle) {
      await handle.handleRemoteDescription(description)
    }
  }

  async function handleRemoteIceCandidate(
    peerId: string,
    candidate: RTCIceCandidateInit,
  ): Promise<void> {
    const handle = peers.get(peerId)
    if (handle) {
      await handle.handleIceCandidate(candidate)
    }
  }

  function relaySdp(
    _fromPeerId: string,
    msg: SdpOfferMessage | SdpAnswerMessage,
  ): void {
    const targetHandle = peers.get(msg.payload.targetPeerId)
    if (targetHandle) {
      try {
        sendMessage(targetHandle.channels.signaling, msg)
      } catch {
        // Target channel not ready
      }
    }
    events.onSdpRelay?.(msg.payload.targetPeerId, msg)
  }

  function relayIceCandidate(
    _fromPeerId: string,
    msg: IceCandidateMessage,
  ): void {
    const targetHandle = peers.get(msg.payload.targetPeerId)
    if (targetHandle) {
      try {
        sendMessage(targetHandle.channels.signaling, msg)
      } catch {
        // Target channel not ready
      }
    }
  }

  function broadcastSignaling(type: string, payload: unknown): void {
    const msg = createEnvelope(type, localPeerId, payload)
    for (const handle of peers.values()) {
      try {
        sendMessage(handle.channels.signaling, msg)
      } catch {
        // Channel may not be open
      }
    }
  }

  function broadcastHealth(type: string, payload: unknown): void {
    const msg = createEnvelope(type, localPeerId, payload)
    for (const handle of peers.values()) {
      try {
        sendMessage(handle.channels.health, msg)
      } catch {
        // Channel may not be open
      }
    }
  }

  function sendToSignaling(peerId: string, type: string, payload: unknown): void {
    const handle = peers.get(peerId)
    if (handle) {
      const msg = createEnvelope(type, localPeerId, payload)
      sendMessage(handle.channels.signaling, msg)
    }
  }

  function broadcastParticipantJoined(info: ParticipantJoinedMessage['payload']): void {
    broadcastSignaling('participant-joined', info)
  }

  function broadcastParticipantLeft(reason: 'user-left' | 'kicked' | 'error'): void {
    broadcastSignaling('participant-left', { reason })
  }

  function broadcastParticipantUpdate(update: ParticipantUpdateMessage['payload']): void {
    broadcastSignaling('participant-update', update)
  }

  function sendMeetingStateSync(
    peerId: string,
    payload: MeetingStateSyncMessage['payload'],
  ): void {
    sendToSignaling(peerId, 'meeting-state-sync', payload)
  }

  // --- Heartbeats ---

  function startHeartbeats(): void {
    if (heartbeatInterval) return

    joinedAt = Date.now()

    heartbeatInterval = setInterval(() => {
      const connectedPeerIds = Array.from(peers.keys())
      broadcastHealth('heartbeat', {
        uptimeMs: Date.now() - joinedAt,
        connectedPeers: connectedPeerIds,
      })
    }, HEARTBEAT_INTERVAL_MS)

    // Start bandwidth reports every 10s
    bandwidthReportInterval = setInterval(() => {
      reportBandwidth()
    }, BANDWIDTH_REPORT_INTERVAL_MS)

    // Start heartbeat timers for all existing peers
    for (const handle of peers.values()) {
      ;(handle as PeerConnectionHandleInternal).resetHeartbeatTimer()
    }
  }

  function stopHeartbeats(): void {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
    if (bandwidthReportInterval) {
      clearInterval(bandwidthReportInterval)
      bandwidthReportInterval = null
    }
  }

  // --- Bandwidth reporting ---

  async function reportBandwidth(): Promise<void> {
    // Aggregate stats from all peers and broadcast
    let totalUploadKbps = 0
    // Download stats require inbound aggregation — omitted for now
    let avgRtt = 0
    let avgPacketLoss = 0
    let count = 0

    for (const handle of peers.values()) {
      const snapshot = handle.getQualitySnapshot()
      if (snapshot) {
        if (snapshot.availableBitrate !== null) {
          totalUploadKbps += snapshot.availableBitrate / 1000
        }
        if (snapshot.rtt !== null) {
          avgRtt += snapshot.rtt * 1000 // Convert to ms
        }
        avgPacketLoss += snapshot.packetLossRate * 100
        count++
      }
    }

    if (count === 0) return

    broadcastHealth('bandwidth-report', {
      uploadKbps: Math.round(totalUploadKbps / count),
      downloadKbps: 0, // Requires inbound stats aggregation
      rttMs: Math.round(avgRtt / count),
      packetLossPercent: +(avgPacketLoss / count).toFixed(2),
      measuredAt: Date.now(),
    })
  }

  function getQuality(peerId: string): QualitySnapshot | null {
    return peers.get(peerId)?.getQualitySnapshot() ?? null
  }

  function peerCount(): number {
    return peers.size + 1 // +1 for self
  }

  function canAcceptPeer(): boolean {
    return peerCount() < MAX_PARTICIPANTS
  }

  function destroy(): void {
    stopHeartbeats()
    for (const [peerId] of peers) {
      removePeer(peerId, 'left')
    }
  }

  return {
    localPeerId,
    addPeer,
    removePeer,
    getPeer,
    getPeers,
    getPeerInfo,
    updatePeerInfo,
    addTracksToAll,
    handleRemoteSdp,
    handleRemoteIceCandidate,
    relaySdp,
    relayIceCandidate,
    broadcastSignaling,
    broadcastHealth,
    sendToSignaling,
    broadcastParticipantJoined,
    broadcastParticipantLeft,
    broadcastParticipantUpdate,
    sendMeetingStateSync,
    startHeartbeats,
    stopHeartbeats,
    getQuality,
    peerCount,
    canAcceptPeer,
    destroy,
  }
}
